// Vstupní bod embed skriptu — skládá dohromady konfiguraci, UI, uložení,
// script gating a Google Consent Mode v2. Vystavuje window.CookieGobbler
// (zmrazená fasáda) a frontu window.cgQueue.
import { I18n, resolveLang } from './i18n';
import { loadConsent, saveConsent, clearConsent } from './storage';
import { initConsentDefaults, updateConsent } from './consent-mode';
import { applyGating } from './gating';
import { UI } from './ui';
import { renderTrigger } from './trigger';
import { readUrlConsent, stripUrlConsent, isAllRejected } from './url-consent';
import { installQueue } from './queue';
import type {
  CategoryDef,
  CategoryId,
  ConsentSource,
  ConsentState,
  CookieGobblerConfig,
  StoredConsent,
} from './types';

const DEFAULT_CATEGORIES: CategoryDef[] = [
  { id: 'necessary', required: true, signals: ['security_storage'] },
  {
    id: 'preferences',
    signals: ['functionality_storage', 'personalization_storage'],
  },
  { id: 'statistics', signals: ['analytics_storage'] },
  {
    id: 'marketing',
    signals: ['ad_storage', 'ad_user_data', 'ad_personalization'],
  },
];

type Listener = (state: ConsentState) => void;

/** Veřejné API na window.CookieGobbler. Udělit souhlas z kódu nejde (BR-1). */
export interface CookieGobblerApi {
  /**
   * Odmítne vše nepovinné za návštěvníka (zdroj 'app'). Před startem embedu se
   * požadavek podrží a provede místo zobrazení banneru. Resolve: aktuální stav
   * po odmítnutí; null = CookieGobbler neběží. Promise se nikdy nerejectuje.
   */
  reject(): Promise<ConsentState | null>;
  openSettings(): void;
  reset(): void;
  withdraw(): void;
  renderTrigger(target?: HTMLElement | string): void;
  getConsent(): ConsentState;
  hasConsent(category: CategoryId): boolean;
  onChange(fn: Listener): void;
}

class CookieGobbler {
  private config!: CookieGobblerConfig;
  private i18n!: I18n;
  private ui!: UI;
  private state!: ConsentState;
  private stored: StoredConsent | null = null;
  private triggers: HTMLElement[] = [];
  private listeners: Listener[] = [];
  private ready = false;
  private failed = false;
  /** reject() zavolaný před startem — provede se místo banneru (BR-4). */
  private pendingReject = false;
  private pendingResolvers: Array<(s: ConsentState | null) => void> = [];

  /** Zavolá se automaticky co nejdřív pro Consent Mode defaults. */
  bootDefaults(): void {
    // Consent Mode defaults nastavíme hned, i před načtením configu,
    // aby gtag/GA4 startovaly v "denied" stavu.
    initConsentDefaults();
  }

  async init(config: CookieGobblerConfig): Promise<void> {
    this.config = {
      googleConsentMode: true,
      consentExpiryDays: 180,
      defaultLang: 'auto',
      ...config,
    };
    if (!this.config.categories?.length) {
      this.config.categories = DEFAULT_CATEGORIES;
    }

    const lang = resolveLang(this.config.languages, this.config.defaultLang);
    this.i18n = new I18n(lang, this.config.texts?.[lang]);
    this.state = this.blankState();

    const run = () => this.start();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run, { once: true });
    } else {
      run();
    }
  }

  private start(): void {
    this.ui = new UI(this.config, this.i18n, {
      onAcceptAll: () => this.acceptAll(),
      onRejectAll: () => this.rejectAll(),
      onSave: (s) => this.save(s),
      getCurrent: () => this.state,
    });

    const stored = loadConsent(
      this.config.version,
      this.config.consentExpiryDays!,
    );
    if (stored) {
      this.stored = stored;
      this.state = stored.state;
    }
    // Odmítnutí při startu: ?cg_consent=reject (WebView) má přednost před
    // reject() z aplikace (D-6). Uložený souhlas se pak vůbec neaplikuje, gated
    // skripty se nespustí (BR-6). Už odmítnuté se znovu neukládá ani neloguje (BR-7).
    const requested: ConsentSource | null =
      readUrlConsent(location.href) === 'reject' ? 'url' : this.pendingReject ? 'app' : null;
    if (requested && !(stored && isAllRejected(stored.state, this.config.categories))) {
      this.commit(this.blankState(), requested);
    } else if (stored) {
      this.enforce(stored);
      this.ui.showReopen();
    } else {
      this.ui.showBanner();
    }
    this.removeUrlParam();
    this.ready = true;
    this.mountTriggers();
    this.settle();
  }

  /** Vyřídí čekající reject(): aktuální stav, nebo null, když neběžíme (BR-10, BR-11). */
  private settle(): void {
    const resolvers = this.pendingResolvers;
    this.pendingResolvers = [];
    for (const resolve of resolvers) resolve(this.failed ? null : this.getConsent());
  }

  /** Konfigurace se nenačetla — čekající reject() dostanou null, nic se neloguje (BR-11). */
  fail(): void {
    this.failed = true;
    this.settle();
  }

  /** Odstraní cg_consent z adresy, ať se při sdílení odkazu nešíří dál. */
  private removeUrlParam(): void {
    const clean = stripUrlConsent(location.href);
    if (!clean) return;
    try {
      history.replaceState(history.state, '', clean);
    } catch {
      /* sandbox bez History API — parametr v URL zůstane */
    }
  }

  // ---- Akce ---------------------------------------------------------------

  // Akce návštěvníka (banner, modál, widget) → zdroj 'visitor' (BR-12).
  private acceptAll(): void {
    const s = this.blankState();
    for (const c of this.config.categories) s[c.id] = true;
    this.commit(s, 'visitor');
  }

  private rejectAll(): void {
    this.commit(this.blankState(), 'visitor');
  }

  private save(state: ConsentState): void {
    const s = { ...this.blankState(), ...state };
    // Nutné vždy zapnuté.
    for (const c of this.config.categories) if (c.required) s[c.id] = true;
    this.commit(s, 'visitor');
  }

  /** Odmítnutí jako „Odmítnout vše" (BR-2); zavře banner i modál (BR-5). */
  private applyReject(source: ConsentSource): void {
    if (this.stored && isAllRejected(this.stored.state, this.config.categories)) {
      this.ui.showReopen(); // BR-7: už odmítnuté se znovu neukládá ani neloguje
      return;
    }
    this.commit(this.blankState(), source);
  }

  private commit(state: ConsentState, source: ConsentSource): void {
    this.state = state;
    const record = saveConsent(
      state,
      this.config.version,
      this.i18n.lang,
      this.config.consentExpiryDays!,
    );
    this.stored = record;
    this.enforce(record);
    this.logConsent(record, source);
    this.ui.showReopen();
    this.refreshTriggers();
  }

  /** Aplikuje souhlas: Consent Mode update, script gating, listeners. */
  private enforce(record: StoredConsent): void {
    if (this.config.googleConsentMode) {
      updateConsent(record.state, this.config.categories);
    }
    applyGating(record.state);
    this.listeners.forEach((fn) => fn(record.state));
  }

  private logConsent(record: StoredConsent, source: ConsentSource): void {
    if (!this.config.logEndpoint) return;
    const payload = JSON.stringify({
      consentId: record.id,
      configId: this.config.id,
      version: record.version,
      state: record.state,
      lang: record.lang,
      timestamp: record.timestamp,
      url: location.href,
      source, // BR-8
    });
    // Beacon jako text/plain (řetězec) = CORS-safelisted → bez preflightu, takže
    // cross-origin sendBeacon spolehlivě doručí. Blob s application/json by
    // preflight vynutil a jako beacon spadne na CORS (log se ztratí).
    try {
      if (navigator.sendBeacon(this.config.logEndpoint, payload)) return;
    } catch {
      /* fallback na fetch níže */
    }
    fetch(this.config.logEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: payload,
      keepalive: true,
      mode: 'cors',
    }).catch(() => void 0);
  }

  private blankState(): ConsentState {
    const s = {} as ConsentState;
    for (const c of this.config.categories) s[c.id] = !!c.required;
    return s;
  }

  // ---- Veřejné API --------------------------------------------------------

  /** Odmítnutí z webové aplikace (zdroj 'app'), viz CookieGobblerApi.reject. */
  reject(): Promise<ConsentState | null> {
    if (this.failed) return Promise.resolve(null);
    if (!this.ready) {
      this.pendingReject = true;
      return new Promise((resolve) => this.pendingResolvers.push(resolve));
    }
    this.applyReject('app');
    return Promise.resolve(this.getConsent());
  }

  /** Otevře nastavení (druhá vrstva). */
  openSettings(): void {
    if (this.ready) this.ui.showModal(this.state);
  }

  /** Znovu vyžádá souhlas (např. po změně configu). */
  reset(): void {
    clearConsent();
    this.stored = null;
    if (this.ready) this.ui.showBanner();
    this.refreshTriggers();
  }

  /** Odvolá souhlas — zaloguje odmítnutí všeho a aktualizuje widgety. */
  withdraw(): void {
    if (this.ready) this.rejectAll();
  }

  /**
   * Vyrenderuje „Privacy trigger" widget do zadaného prvku (nebo do všech
   * prvků s atributem [data-cg-trigger], pokud target není zadán).
   */
  renderTrigger(target?: HTMLElement | string): void {
    if (!target) {
      this.mountTriggers();
      return;
    }
    const el = typeof target === 'string' ? document.querySelector<HTMLElement>(target) : target;
    if (el) this.renderOneTrigger(el);
  }

  private mountTriggers(): void {
    document
      .querySelectorAll<HTMLElement>('[data-cg-trigger]')
      .forEach((el) => this.renderOneTrigger(el));
  }

  private renderOneTrigger(el: HTMLElement): void {
    if (!this.triggers.includes(el)) this.triggers.push(el);
    renderTrigger(el, this.i18n, this.config, this.stored, {
      onChange: () => this.ui.showModal(this.state),
      onWithdraw: () => this.withdraw(),
    });
  }

  private refreshTriggers(): void {
    for (const el of this.triggers) this.renderOneTrigger(el);
  }

  /** Aktuální stav souhlasu. */
  getConsent(): ConsentState {
    return { ...this.state };
  }

  /** True, pokud daná kategorie má souhlas. */
  hasConsent(category: keyof ConsentState): boolean {
    return !!this.state[category];
  }

  /** Registruje callback volaný při každé změně souhlasu. */
  onChange(fn: Listener): void {
    this.listeners.push(fn);
    if (this.ready) fn(this.state);
  }
}

/**
 * Zmrazená fasáda jen s veřejnými metodami. TS `private` platí jen při kompilaci,
 * instance na window by vystavila i acceptAll/save/commit (BR-1).
 */
export function createPublicApi(cg: CookieGobbler): CookieGobblerApi {
  return Object.freeze({
    reject: () => cg.reject(),
    openSettings: () => cg.openSettings(),
    reset: () => cg.reset(),
    withdraw: () => cg.withdraw(),
    renderTrigger: (target?: HTMLElement | string) => cg.renderTrigger(target),
    getConsent: () => cg.getConsent(),
    hasConsent: (category: CategoryId) => cg.hasConsent(category),
    onChange: (fn: Listener) => cg.onChange(fn),
  });
}

const instance = new CookieGobbler();
instance.bootDefaults();

// Auto-init z:
//  1) <script data-cg-config="URL">          — konkrétní konfigurace
//  2) <script data-cg-api="API_BASE">        — resolve podle aktuální domény
//     (skupina domén/aliasy → /api/config/by-host/<hostname>)
//  3) window.cgConfig                          — inline konfigurace
// Odvodí základ API (kam posílat consent log) z adresy, odkud se načetl config.
// data-cg-api → přímo; data-cg-config → část před "/api/config/" (jinak origin).
export function deriveApiBase(configUrl: string | null, apiBase: string | null): string | null {
  if (apiBase) return apiBase.replace(/\/$/, '');
  if (configUrl) {
    const i = configUrl.indexOf('/api/config/');
    if (i >= 0) return configUrl.slice(0, i);
    try {
      return new URL(configUrl, location.href).origin;
    } catch {
      return null;
    }
  }
  return null;
}

async function autoInit(): Promise<void> {
  const findAttr = (name: string) =>
    (document.currentScript as HTMLScriptElement | null)?.getAttribute(name) ??
    document.querySelector(`script[${name}]`)?.getAttribute(name) ??
    null;

  const configUrl = findAttr('data-cg-config');
  const apiBase = findAttr('data-cg-api');
  const url = configUrl
    ? configUrl
    : apiBase
      ? `${apiBase.replace(/\/$/, '')}/api/config/by-host/${encodeURIComponent(location.hostname)}`
      : null;

  if (url) {
    try {
      const res = await fetch(url, { credentials: 'omit' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const cfg = (await res.json()) as CookieGobblerConfig;
      // Consent log: když config sám neurčuje endpoint, pošli souhlasy zpět na
      // API, odkud se config načetl — jinak by se nikam nelogovaly (statistiky 0).
      const logBase = deriveApiBase(configUrl, apiBase);
      if (logBase && cfg.logEndpoint === undefined) {
        cfg.logEndpoint = `${logBase}/api/consent`;
      }
      await instance.init(cfg);
    } catch (e) {
      console.error('[CookieGobbler] Nepodařilo se načíst konfiguraci:', e);
      instance.fail();
    }
  } else if ((window as unknown as { cgConfig?: CookieGobblerConfig }).cgConfig) {
    await instance.init(
      (window as unknown as { cgConfig: CookieGobblerConfig }).cgConfig,
    );
  } else {
    instance.fail(); // bez zdroje konfigurace CookieGobbler neběží
  }
}

const api = createPublicApi(instance);
(window as unknown as { CookieGobbler: CookieGobblerApi }).CookieGobbler = api;
installQueue(window, api);
void autoInit();

export type { CookieGobblerConfig, ConsentState };
export { CookieGobbler };
